// Recap import — handles every platform and lands on a single pipelineState.
//
// Sleeper path: username → user's leagues → pick league → fetchSleeperLeagueHistory
//   with onProgress driving a per-season indicator → sleeperToMatchups +
//   sleeperToChampionships + computeFunStats → emit pipelineState.
//
// Yahoo path: OAuth popup (?mode=recap) → user's leagues → pick current league
//   → /api/yahoo/history for prior-season discovery → user confirms which to
//   include → emit pipelineState with { phase: 'yahoo-discovered', token,
//   selectedSeasons } for a downstream task (#13) to do the per-season fetch.
//
// NO ESPN PATH, DELIBERATELY. It lived here briefly (ac0c48c) and moved to
// SettingsModal.jsx, because ESPN's two calls fetch settings and draftDetail
// ONLY — no matchups, so there was never an almanac to build and the flow
// dead-ended on RecapReport's InsufficientDataState. What it does produce, a
// configured league plus its auction history, is what the settings import is
// for. The allowlist below is what makes this absence SAFE: 'espn' now falls to
// UnknownPlatform instead of silently opening Yahoo's OAuth popup.
//
// ⚠️ THE FLOW SELECTOR IS AN ALLOWLIST, NOT A FALLBACK. It used to be
// `platform === "sleeper" ? <SleeperFlow/> : <YahooFlow/>`, which is not a
// choice between two platforms — it is "Sleeper, or else Yahoo". Every value
// that was not exactly "sleeper" opened the Yahoo OAuth popup, so adding a
// third picker card would have sent those users to a login for an account they
// may not even have. A map plus an explicit unknown-platform panel means a new
// platform that is wired in the picker but not here FAILS VISIBLY instead of
// impersonating Yahoo. Do not reintroduce a default branch that renders a real
// flow.
//
// Universal UI patterns are reused from src/components/SettingsModal.jsx
// (sleeperStep state shape, Yahoo OAuth popup + postMessage pattern).

import { useState, useEffect, useRef, useMemo } from "react";
import theme from "../../data/theme";
import { fetchSleeperUserLeagues, fetchSleeperLeagueHistory, SLEEPER_EARLIEST_STATS_YEAR } from "../../utils/sleeperImport";
import { fetchYahooLeagueHistory, fetchYahooTeamsForLeagues } from "../../utils/yahooImport";
import { sleeperToMatchups, sleeperToChampionships, sleeperToDrafts, sleeperToTrades, yahooToMatchups, yahooToChampionships, yahooToDrafts, yahooToTrades, detectSameSeasonCollisions } from "../../utils/recapAdapters";
import { computeFunStats } from "../../engine/funStats";
import { attachRecordLineups } from "../../utils/lineupPrePull";
import { captureLeagueImported, captureFetchFailed, registerLeagueHash, captureAlmanacFetchCompleted, captureAlmanacFetchFailed, captureSleeperLeagueCaptured, captureSleeperUserLookedUp } from "../../utils/recapAnalytics";
import { suggestCanonicalName, extractRawOwners } from "../../utils/aliasHeuristics";
import { upfrontEstimateMinutes, rollingEtaSec, fmtRemaining } from "../../utils/recapEta";
import { YAHOO_UNAVAILABLE_LEAD, YAHOO_UNAVAILABLE_BODY } from "../../data/platformStatus";

const TEXT_INPUT = {
  width: "100%",
  background: theme.colors.bg.surface,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 8,
  padding: "10px 12px",
  color: theme.colors.text.primary,
  fontFamily: theme.fonts.body,
  fontSize: 14,
};

const PRIMARY_BTN = {
  background: theme.colors.brand.gradientBtn,
  color: "#fff",
  border: "none",
  padding: "10px 22px",
  fontSize: 13,
  fontWeight: theme.fontWeight.heavy,
  fontFamily: theme.fonts.display,
  letterSpacing: "1.5px",
  borderRadius: 10,
  cursor: "pointer",
};

const SECONDARY_BTN = {
  background: "transparent",
  color: theme.colors.text.muted,
  border: `1px solid ${theme.colors.border.default}`,
  padding: "8px 16px",
  fontSize: 12,
  fontWeight: theme.fontWeight.medium,
  fontFamily: theme.fonts.display,
  letterSpacing: "1px",
  borderRadius: 8,
  cursor: "pointer",
};

const ERROR_BOX = {
  background: "rgba(239,68,68,0.10)",
  border: `1px solid rgba(239,68,68,0.35)`,
  color: theme.colors.status.danger,
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  fontFamily: theme.fonts.body,
};

const PANEL = {
  background: theme.colors.bg.raised,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 14,
  padding: "24px 26px",
  width: "100%",
  maxWidth: 560,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

// The one place a platform becomes a flow. Function declarations hoist, so the
// map can name them before they appear below.
const FLOWS = {
  sleeper: SleeperFlow,
  yahoo: YahooFlow,
};

// Exported so the router in AuctionDraftBuilder.jsx validates against the SAME
// list this file dispatches on, rather than keeping its own copy that can drift.
export const RECAP_PLATFORMS = Object.keys(FLOWS);

export default function RecapImport({ platform, onBack, onPipelineReady }) {
  const Flow = FLOWS[platform];
  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: theme.fonts.display,
    }}>
      <div style={{ position: "absolute", top: 16, left: 20 }}>
        <button onClick={onBack} style={{ ...SECONDARY_BTN, padding: "6px 14px" }}>
          ← Back
        </button>
      </div>

      {Flow
        ? <Flow onPipelineReady={onPipelineReady} />
        : <UnknownPlatform platform={platform} onBack={onBack} />}
    </div>
  );
}

// The defensive default. Reachable only if a picker offers a platform this file
// does not implement — a wiring bug, so it says so plainly rather than guessing
// at a flow. Silently routing to a real platform is how the old ternary turned
// a missing case into a Yahoo login prompt.
function UnknownPlatform({ platform, onBack }) {
  return (
    <div style={PANEL}>
      <SectionHeader
        title="Unknown platform"
        subtitle={`"${String(platform)}" isn't a platform this import supports. That's a bug on our side, not something you did.`}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={PRIMARY_BTN} onClick={onBack}>← Pick a platform</button>
      </div>
    </div>
  );
}

// ============================================================================
// SLEEPER
// ============================================================================

function SleeperFlow({ onPipelineReady }) {
  // 'username' → 'picking' → 'fetching' → 'aliasing' (NEW, task #26)
  //   → adapter+pipeline → emit; 'error' on failure
  const [step, setStep] = useState("username");
  const [username, setUsername] = useState("");
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ message: "", current: 0, total: null });
  // Holds the fetched seasons + picked league between the fetch and the
  // alias step so adapter+pipeline run can fire on alias confirm.
  const [fetchedSeasons, setFetchedSeasons] = useState(null);
  const [pickedLeague, setPickedLeague] = useState(null);
  const [fetchStartMs, setFetchStartMs] = useState(null);

  const handleFindLeagues = async () => {
    if (!username.trim()) return;
    setLoading(true);
    setError(null);
    const { leagues: found, error: err, userId, season } = await fetchSleeperUserLeagues(username.trim());
    setLoading(false);
    if (err) {
      captureFetchFailed({ platform: "sleeper", stage: "users", errorMessage: err });
      setError(err);
      return;
    }
    setLeagues(found || []);
    captureSleeperUserLookedUp({ username: username.trim(), userId, season, leagues: found });
    setStep("picking");
  };

  const handlePickLeague = async (league) => {
    setStep("fetching");
    setError(null);
    setProgress({ message: `Starting walkback for ${league.name}...`, current: 0, total: null });

    const t0 = performance.now();
    setFetchStartMs(t0);
    const { seasons, error: err, cacheHits, cacheMisses } = await fetchSleeperLeagueHistory(league.league_id, {
      onProgress: (message, current, total) => {
        setProgress({ message, current, total });
      },
    });
    const elapsedSec = Math.round((performance.now() - t0) / 1000);

    if (err) {
      captureFetchFailed({ platform: "sleeper", stage: "history", errorMessage: err });
      captureAlmanacFetchFailed({ platform: "sleeper", seasonCount: 0, elapsedSoFar: elapsedSec, errorReason: err });
      setError(err);
      setStep("error");
      return;
    }
    if (!seasons?.length) {
      captureFetchFailed({ platform: "sleeper", stage: "history", errorMessage: "No seasons returned" });
      captureAlmanacFetchFailed({ platform: "sleeper", seasonCount: 0, elapsedSoFar: elapsedSec, errorReason: "No seasons returned" });
      setError("No seasons returned for this league.");
      setStep("error");
      return;
    }

    captureAlmanacFetchCompleted({
      platform: "sleeper",
      seasonCount: seasons.length,
      elapsedSeconds: elapsedSec,
      leagueName: league.name,
      cacheHits: cacheHits || 0,
      cacheMisses: cacheMisses || 0,
    });

    // seasons is newest-first, so seasons[0] carries this league's own season
    // and its previous_league_id — both already fetched by the walkback.
    captureSleeperLeagueCaptured({
      leagueId: league.league_id,
      leagueName: league.name,
      season: seasons[0]?.season,
      source: "almanac",
      previousLeagueId: seasons[0]?.previous_league_id,
    });

    setFetchedSeasons(seasons);
    setPickedLeague(league);
    setStep("aliasing");
  };

  const handleAliasConfirm = async (aliasMap) => {
    const seasons = fetchedSeasons;
    const league = pickedLeague;
    if (!seasons || !league) return;
    try {
      const matchups = sleeperToMatchups(seasons, { aliasMap });
      const championshipsByYear = sleeperToChampionships(seasons, { aliasMap });
      const drafts = sleeperToDrafts(seasons, { aliasMap });
      const trades = sleeperToTrades(seasons, { aliasMap });
      const stats = computeFunStats(matchups, { championshipsByYear, drafts, trades, earliestStatsYear: SLEEPER_EARLIEST_STATS_YEAR });

      // A2 lineup detail — pre-pull lineups for record games. Sleeper is
      // free (data already in season.matchupsByWeek per Phase 0 audit).
      try {
        await attachRecordLineups({
          stats, seasons, platform: "sleeper",
          leagueId: league.league_id, aliasMap,
          onProgress: (msg, c, t) => setProgress({ message: msg, current: c, total: t }),
        });
      } catch (e) {
        // Pre-pull failures are non-fatal — the lazy-fetch path covers
        // missing lineups on click. Log and continue to render.
        // eslint-disable-next-line no-console
        console.warn("Lineup pre-pull failed:", e?.message);
      }

      const leagueIdHash = await registerLeagueHash(league.league_id);
      captureLeagueImported({
        platform: "sleeper",
        leagueIdHash,
        seasonCount: seasons.length,
        ownerCount: stats.meta.totalOwners,
        fetchDurationMs: Math.round(performance.now() - (fetchStartMs || performance.now())),
      });

      onPipelineReady({
        platform: "sleeper",
        leagueMetadata: {
          name: league.name || seasons[0]?.name || "League",
          yearsCovered: stats.meta.yearsCovered,
          ownerCount: stats.meta.totalOwners,
        },
        seasons,
        matchups,
        championshipsByYear,
        stats,
        // Carried forward for the JSON download — buildAlmanacExport needs
        // it to construct the per-canonical owner aliases list.
        aliasMap,
      });
    } catch (e) {
      captureFetchFailed({ platform: "sleeper", stage: "pipeline", errorMessage: e?.message });
      setError(e?.message || "Pipeline error");
      setStep("error");
    }
  };

  return (
    <div style={PANEL}>
      <SectionHeader title="Sleeper recap" subtitle="Enter your Sleeper username — we'll find your leagues." />

      {step === "username" && (
        <>
          <input
            style={TEXT_INPUT}
            value={username}
            onChange={(e) => { setUsername(e.target.value); setError(null); }}
            onKeyDown={(e) => e.key === "Enter" && handleFindLeagues()}
            placeholder="sleeper username"
            autoFocus
          />
          {error && <div style={ERROR_BOX}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              style={{ ...PRIMARY_BTN, opacity: loading || !username.trim() ? 0.5 : 1 }}
              disabled={loading || !username.trim()}
              onClick={handleFindLeagues}
            >
              {loading ? "Looking up..." : "Find my leagues"}
            </button>
          </div>
        </>
      )}

      {step === "picking" && (
        <>
          <div style={{
            color: theme.colors.text.muted, fontSize: 13, fontFamily: theme.fonts.body,
          }}>
            {leagues.length === 0
              ? "No NFL leagues found for this username."
              : `Found ${leagues.length} league${leagues.length !== 1 ? "s" : ""} — pick one:`}
            <span
              style={{ marginLeft: 8, cursor: "pointer", color: theme.colors.brand.accent }}
              onClick={() => { setStep("username"); setError(null); }}
            >
              change user
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
            {leagues.map((l) => (
              <button
                key={l.league_id}
                onClick={() => handlePickLeague(l)}
                style={{
                  background: theme.colors.bg.surface,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.body,
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <strong>{l.name || "(unnamed)"}</strong>
                  <span style={{ color: theme.colors.text.muted, marginLeft: 8 }}>
                    {l.total_rosters}-team
                  </span>
                </span>
                <span style={{ color: theme.colors.brand.accent, fontSize: 11, letterSpacing: 1 }}>
                  PICK →
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === "fetching" && (
        <FetchProgress
          title="Building your league's almanac..."
          message={progress.message}
          current={progress.current}
          total={progress.total}
        />
      )}

      {step === "aliasing" && fetchedSeasons && (
        <AliasPanel
          rawNames={extractRawOwners("sleeper", fetchedSeasons)}
          leagueId={pickedLeague?.league_id}
          platform="sleeper"
          onConfirm={handleAliasConfirm}
        />
      )}

      {step === "error" && (
        <>
          <div style={ERROR_BOX}>{error || "Something went wrong."}</div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={SECONDARY_BTN} onClick={() => setStep("picking")}>Try another league</button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// YAHOO
// ============================================================================

function YahooFlow({ onPipelineReady }) {
  // 'idle' → 'waiting' (OAuth popup) → 'picking' (leagues returned) →
  // 'discovering' (/api/yahoo/history) → 'confirming' (season checkboxes) →
  // 'fetching' (per-season teams + standings + scoreboards via the standalone
  //   /api/yahoo/* endpoints, rate-limited at ~1 req/sec) → pipelineState
  // matches the Sleeper shape so downstream has no platform branching.
  const [step, setStep] = useState("idle");
  const [error, setError] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [token, setToken] = useState(null);
  const [currentLeague, setCurrentLeague] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [otherLeagues, setOtherLeagues] = useState([]);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState({ message: "", current: 0, total: null });
  // Selected league list + teams-only prelude data carry state across the
  // alias step (task #26). Confirming aliases triggers the full per-season
  // fetch using `confirmedLeagues`.
  const [confirmedLeagues, setConfirmedLeagues] = useState(null);
  const [seasonsTeams, setSeasonsTeams] = useState(null);

  const popupRef = useRef(null);
  const pollRef = useRef(null);
  // Grace timer: Yahoo can auto-approve and close the OAuth popup BEFORE its
  // postMessage reaches us, so a bare popup.closed check false-declares
  // "cancelled". We wait out a short grace period for a late message.
  const graceRef = useRef(null);
  // Edge #6 — debounce the league-pick + season-confirm clicks. While a
  // history fetch or per-season fetch is in flight, double-clicks would
  // otherwise fire concurrent calls and race.
  const [pickInFlight, setPickInFlight] = useState(false);

  useEffect(() => () => { clearInterval(pollRef.current); clearTimeout(graceRef.current); }, []);

  // Edge #5 — beforeunload warning during the slow per-season fetch. Refresh
  // would otherwise dump 1-2 minutes of Yahoo work and force a full re-OAuth.
  useEffect(() => {
    if (step !== "fetching" && step !== "aliasing-loading") return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Your league almanac is still building — refresh anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [step]);

  // Intentionally uncalled while Yahoo API access is revoked — the "idle" step
  // below renders the unavailable notice instead of a sign-in button. Kept, not
  // deleted: this is the whole OAuth launch and restoring access is re-wiring
  // one onClick to it.
  // eslint-disable-next-line no-unused-vars
  const handleLogin = () => {
    setStep("waiting");
    setError(null);
    const popup = window.open("/api/yahoo/auth?mode=recap", "yahoo-recap-auth", "width=600,height=700");
    popupRef.current = popup;

    const onMessage = (e) => {
      // Edge #1 — Yahoo redirects with ?error= when user denies/cancels
      // consent; callback posts yahoo-recap-error so we don't wait forever.
      if (e.data?.type === "yahoo-recap-error") {
        window.removeEventListener("message", onMessage);
        clearInterval(pollRef.current);
        clearTimeout(graceRef.current);
        captureFetchFailed({ platform: "yahoo", stage: "oauth", errorMessage: e.data.message || "Access denied" });
        setError(`Yahoo sign-in was cancelled${e.data.message ? `: ${e.data.message}` : "."}`);
        setStep("idle");
        return;
      }
      if (e.data?.type !== "yahoo-recap") return;
      window.removeEventListener("message", onMessage);
      clearInterval(pollRef.current);
      clearTimeout(graceRef.current); // a late message beat the grace-cancel
      // A1 Patch A — client-side belt-and-suspenders sort. Server-side
      // dedup + sort lives in api/yahoo/callback.js, but defending in
      // depth here protects against any future shape drift or message
      // re-ordering during the postMessage hop.
      const incoming = Array.isArray(e.data.leagues) ? e.data.leagues : [];
      const seen = new Set();
      const sortedDeduped = [];
      for (const l of incoming) {
        if (!l?.league_key || seen.has(l.league_key)) continue;
        seen.add(l.league_key);
        sortedDeduped.push(l);
      }
      sortedDeduped.sort((a, b) => Number(b.season) - Number(a.season));
      setLeagues(sortedDeduped);
      setToken(e.data.token);
      setStep("picking");
    };
    window.addEventListener("message", onMessage);

    pollRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(pollRef.current);
        // Yahoo auto-approves repeat logins and closes the popup before its
        // postMessage lands — a bare closed-check here false-declares "Login
        // cancelled" (reproduced repeatedly). Keep the message listener alive
        // and wait a grace period; only cancel if still "waiting" after it.
        if (graceRef.current) return;
        graceRef.current = setTimeout(() => {
          graceRef.current = null;
          window.removeEventListener("message", onMessage);
          setStep((prev) => {
            if (prev === "waiting") {
              captureFetchFailed({ platform: "yahoo", stage: "oauth", errorMessage: "Login cancelled or window closed" });
              setError("Login cancelled or window closed.");
              return "idle";
            }
            return prev;
          });
        }, 1800);
      }
    }, 500);
  };

  const handlePickLeague = async (league) => {
    if (pickInFlight) return; // Edge #6 — drop the second click on double-click
    setPickInFlight(true);
    setCurrentLeague(league);
    setStep("discovering");
    setError(null);
    try {
      const res = await fetch(
        `/api/yahoo/history?token=${encodeURIComponent(token)}&league_key=${encodeURIComponent(league.league_key)}`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `History fetch failed (${res.status})`);
      }
      const data = await res.json();
      const cands = data.candidates || [];
      setCandidates(cands);
      setOtherLeagues(data.otherLeagues || []);
      setShowManualAdd(false);
      // Auto-select high-confidence candidates (exact name match OR ≥0.70
      // owner Jaccard). Leave medium-overlap ones unchecked for user judgment
      // — they're surfaced as possibilities but not assumed-relevant.
      // Manual-add leagues are NEVER auto-selected (user must opt in).
      setSelected(new Set(cands
        .filter(c => c.nameMatch === "exact" || c.ownerMatch === "high")
        .map(c => c.league_key)
      ));
      setStep("confirming");
    } catch (e) {
      captureFetchFailed({ platform: "yahoo", stage: "history", errorMessage: e?.message });
      setError(e.message);
      setStep("picking");
    } finally {
      setPickInFlight(false);
    }
  };

  const handleConfirm = async () => {
    // Build the full ordered list — current league first, then user-selected
    // priors (from both auto-detected candidates AND manually-added other
    // leagues). Newest-first so canonical-name resolution picks the latest
    // known nickname per guid.
    const priorSelected = [
      ...candidates.filter((c) => selected.has(c.league_key)),
      ...otherLeagues.filter((l) => selected.has(l.league_key)),
    ];
    const allLeagues = [currentLeague, ...priorSelected].sort(
      (a, b) => String(b.season).localeCompare(String(a.season))
    );
    setConfirmedLeagues(allLeagues);

    // Teams-only prelude — fetches just /teams across the selected seasons
    // so the alias UI can show the actual owner roster before committing to
    // the (slow) per-season fetch. ~1 req/sec, so 5 seasons ≈ 5-6s.
    setStep("aliasing-loading");
    setError(null);
    setProgress({
      message: `Loading owners for ${allLeagues.length} season${allLeagues.length !== 1 ? "s" : ""}...`,
      current: 0,
      total: allLeagues.length,
    });
    const { seasonsTeams: st, error: tErr } = await fetchYahooTeamsForLeagues(allLeagues, token, {
      onProgress: (message, current, total) => setProgress({ message, current, total }),
    });
    if (tErr) {
      captureFetchFailed({ platform: "yahoo", stage: "fetch", errorMessage: tErr });
      setError(tErr);
      setStep("confirming");
      return;
    }
    setSeasonsTeams(st);
    setStep("aliasing");
  };

  const handleAliasConfirm = async (aliasMap) => {
    const allLeagues = confirmedLeagues;
    if (!allLeagues) return;

    setStep("fetching");
    setError(null);
    setProgress({
      message: `Starting per-season fetch for ${allLeagues.length} season${allLeagues.length !== 1 ? "s" : ""}...`,
      current: 0,
      total: null,
    });

    const t0 = performance.now();
    const { seasons, error: err, tokenExpired, partialFailures, cacheHits, cacheMisses } = await fetchYahooLeagueHistory(allLeagues, token, {
      onProgress: (message, current, total) => {
        setProgress({ message, current, total });
      },
    });
    const yahooElapsedSec = Math.round((performance.now() - t0) / 1000);
    if (tokenExpired) {
      // Edge #3 — token expired mid-fetch. Reset to OAuth and prompt reauth.
      captureFetchFailed({ platform: "yahoo", stage: "fetch", errorMessage: "Token expired mid-fetch" });
      captureAlmanacFetchFailed({ platform: "yahoo", seasonCount: allLeagues.length, elapsedSoFar: yahooElapsedSec, errorReason: "Token expired" });
      setError("Your Yahoo session expired during the fetch. Sign in again to retry.");
      setToken(null);
      setStep("idle");
      return;
    }
    if (err) {
      captureFetchFailed({ platform: "yahoo", stage: "fetch", errorMessage: err });
      captureAlmanacFetchFailed({ platform: "yahoo", seasonCount: allLeagues.length, elapsedSoFar: yahooElapsedSec, errorReason: err });
      setError(err);
      setStep("confirming");
      return;
    }
    if (!seasons?.length) {
      captureFetchFailed({ platform: "yahoo", stage: "fetch", errorMessage: "No seasons returned" });
      captureAlmanacFetchFailed({ platform: "yahoo", seasonCount: allLeagues.length, elapsedSoFar: yahooElapsedSec, errorReason: "No seasons returned" });
      setError("No seasons returned.");
      setStep("confirming");
      return;
    }
    // Edge #2 — filter out errored seasons before passing to the adapter.
    // The adapter expects valid teams/scoreboardsByWeek shapes; errored
    // entries have empty placeholders. Surface the count for visibility.
    const goodSeasons = seasons.filter(s => !s.seasonError);
    if (partialFailures > 0) {
      console.warn(`[recap] ${partialFailures} season(s) failed to fetch and were skipped`);
    }
    if (!goodSeasons.length) {
      setError("All seasons failed to fetch. Try again or sign in again.");
      setStep("confirming");
      return;
    }

    try {
      const matchups = yahooToMatchups(goodSeasons, { aliasMap });
      const championshipsByYear = yahooToChampionships(goodSeasons, { aliasMap });
      const drafts = yahooToDrafts(goodSeasons, { aliasMap });
      const trades = yahooToTrades(goodSeasons, { aliasMap });
      const stats = computeFunStats(matchups, { championshipsByYear, drafts, trades, earliestStatsYear: SLEEPER_EARLIEST_STATS_YEAR });

      // A2 lineup detail — Yahoo pre-pull. ~35 record games × 2 teams =
      // ~80s at the 1.1s throttle. Treated as an explicit phase 2 of the
      // progress bar: pre-pull uses its own (current, total) denominator,
      // and the message text ("Pulling lineups for record games...") makes
      // the phase transition obvious. lineupPrePull fires its first
      // onProgress(msg, 0, total) before the loop so the bar visibly
      // resets from the fetch's 100% to phase 2's 0% rather than freezing.
      // Failures are non-fatal; lazy-fetch covers anything that didn't
      // make it through.
      try {
        await attachRecordLineups({
          stats, seasons: goodSeasons, platform: "yahoo",
          leagueId: currentLeague.league_key, token, aliasMap,
          onProgress: (msg, c, t) => setProgress?.({ message: msg, current: c, total: t }),
        });
      } catch (e) {
        if (e?.code === "AUTH") {
          captureFetchFailed({ platform: "yahoo", stage: "lineup-prepull", errorMessage: "Yahoo 401" });
        }
        // eslint-disable-next-line no-console
        console.warn("Yahoo lineup pre-pull failed:", e?.message);
      }

      const elapsedSec = Math.round((performance.now() - t0) / 1000);
      const leagueIdHash = await registerLeagueHash(currentLeague.league_key);
      captureLeagueImported({
        platform: "yahoo",
        leagueIdHash,
        seasonCount: goodSeasons.length,
        ownerCount: stats.meta.totalOwners,
        fetchDurationMs: Math.round(performance.now() - t0),
      });
      captureAlmanacFetchCompleted({
        platform: "yahoo",
        seasonCount: goodSeasons.length,
        elapsedSeconds: elapsedSec,
        leagueName: currentLeague.name,
        cacheHits: cacheHits || 0,
        cacheMisses: cacheMisses || 0,
      });

      onPipelineReady({
        platform: "yahoo",
        leagueMetadata: {
          name: currentLeague.name,
          yearsCovered: stats.meta.yearsCovered,
          ownerCount: stats.meta.totalOwners,
        },
        seasons: goodSeasons,
        matchups,
        championshipsByYear,
        stats,
        // Carried forward for the JSON download — buildAlmanacExport needs
        // it to construct the per-canonical owner aliases list.
        aliasMap,
      });
    } catch (e) {
      captureFetchFailed({ platform: "yahoo", stage: "pipeline", errorMessage: e?.message });
      setError(e?.message || "Pipeline error");
      setStep("confirming");
    }
  };

  return (
    <div style={PANEL}>
      <SectionHeader title="Yahoo recap" subtitle="Sign in to Yahoo to find your leagues." />

      {/* YAHOO IS DISABLED HERE, NOT REMOVED. This screen is currently
          unreachable — recapStage only becomes "importing-yahoo" via
          onPickPlatform from the Almanac card, which is disabled — so this is
          the belt to that braces: if anything ever routes here again, it says
          what happened instead of opening a popup that 403s. Same shared copy as
          the card and the Settings panel. handleLogin and every step below stay
          intact; restoring access is deleting this block. */}
      {step === "idle" && (
        <>
          {error && <div style={ERROR_BOX}>{error}</div>}
          <div style={{ color: theme.colors.text.muted, fontSize: 13, fontFamily: theme.fonts.body, lineHeight: 1.55 }}>
            <strong style={{ color: theme.colors.text.secondary }}>{YAHOO_UNAVAILABLE_LEAD}</strong>{" "}
            {YAHOO_UNAVAILABLE_BODY}
          </div>
        </>
      )}

      {step === "waiting" && (
        <FetchProgress
          title="Waiting for Yahoo login..."
          message="Complete the sign-in in the popup window."
          current={0}
          total={null}
        />
      )}

      {step === "picking" && (
        <>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, fontFamily: theme.fonts.body }}>
            {leagues.length
              ? `Pick the league you want a recap for (${leagues.length} found):`
              : "No NFL leagues found on this account."}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
            {leagues.map((l) => (
              <button
                key={l.league_key}
                onClick={() => handlePickLeague(l)}
                disabled={pickInFlight}
                style={{
                  background: theme.colors.bg.surface,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.body,
                  fontSize: 13,
                  cursor: pickInFlight ? "wait" : "pointer",
                  opacity: pickInFlight ? 0.55 : 1,
                  textAlign: "left",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <strong>{l.name}</strong>
                  <span style={{ color: theme.colors.text.muted, marginLeft: 8 }}>
                    {l.season} · {l.num_teams ?? "?"}-team
                  </span>
                </span>
                <span style={{ color: theme.colors.brand.accent, fontSize: 11, letterSpacing: 1 }}>PICK →</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === "discovering" && (
        <FetchProgress
          title="Looking for prior seasons..."
          message={`Searching your Yahoo history for prior seasons of ${currentLeague?.name || "this league"}.`}
          current={0}
          total={null}
        />
      )}

      {step === "confirming" && (
        <>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, fontFamily: theme.fonts.body }}>
            Found <strong style={{ color: theme.colors.text.primary }}>{candidates.length}</strong>{" "}
            prior season{candidates.length !== 1 ? "s" : ""} that look like the same league. Confirm
            which to include in the recap:
          </div>

          {candidates.length === 0 ? (
            <div style={{
              color: theme.colors.text.muted, fontSize: 13, fontFamily: theme.fonts.body,
              padding: 12, background: theme.colors.bg.surface, borderRadius: 8,
            }}>
              No prior seasons matched. Single-season recap will be generated for{" "}
              {currentLeague?.season}.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
              {candidates.map((c) => (
                <label key={c.league_key} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 12px",
                  background: theme.colors.bg.surface,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: theme.fonts.body, fontSize: 13,
                  color: theme.colors.text.primary,
                }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.league_key)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(c.league_key); else next.delete(c.league_key);
                      setSelected(next);
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    <strong>{c.season}</strong>
                    <span style={{ color: theme.colors.text.muted, marginLeft: 10 }}>{c.name}</span>
                  </span>
                  <CandidateTags candidate={c} />
                </label>
              ))}
            </div>
          )}

          {/* Manual-add (task #25) — collapsible list of NFL leagues from the
              user's history that didn't pass the auto-detect filter. Useful
              when owner overlap fell below 0.40 OR the league name was
              changed beyond year-suffix-stripping. */}
          {otherLeagues.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <button
                onClick={() => setShowManualAdd(!showManualAdd)}
                style={{
                  background: "transparent",
                  border: `1px dashed ${theme.colors.border.default}`,
                  color: theme.colors.text.muted,
                  padding: "8px 12px", borderRadius: 8,
                  fontFamily: theme.fonts.body, fontSize: 12,
                  cursor: "pointer", width: "100%", textAlign: "left",
                }}
              >
                {showManualAdd ? "▾" : "▸"} Add more seasons ({otherLeagues.length} other Yahoo NFL league{otherLeagues.length !== 1 ? "s" : ""} in your account)
              </button>
              {showManualAdd && (
                <div style={{
                  display: "flex", flexDirection: "column", gap: 6,
                  maxHeight: 240, overflowY: "auto", marginTop: 8,
                }}>
                  {otherLeagues.map((l) => (
                    <label key={l.league_key} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px",
                      background: theme.colors.bg.surface,
                      // Dashed border distinguishes manual-add from auto-detected
                      border: `1px dashed ${theme.colors.border.default}`,
                      borderRadius: 8,
                      cursor: "pointer",
                      fontFamily: theme.fonts.body, fontSize: 13,
                      color: theme.colors.text.primary,
                    }}>
                      <input
                        type="checkbox"
                        checked={selected.has(l.league_key)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(l.league_key); else next.delete(l.league_key);
                          setSelected(next);
                        }}
                      />
                      <span style={{ flex: 1 }}>
                        <strong>{l.season}</strong>
                        <span style={{ color: theme.colors.text.muted, marginLeft: 10 }}>{l.name}</span>
                      </span>
                      <span style={{
                        color: theme.colors.text.muted, fontSize: 10, letterSpacing: 1,
                        textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
                        fontFamily: theme.fonts.display,
                      }}>MANUAL</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && <div style={ERROR_BOX}>{error}</div>}

          {/* A1 Patch C — long-fetch advisory. Yahoo's 1.1s/call throttle
              compounds quickly; a 20-season pull is ~8 minutes. Show the
              estimate before the user commits to the heavy fetch so they
              know to leave the tab open. ~0.4 min/season is empirical
              from Smithtown-class leagues (settings + standings + 17
              weeks of scoreboards + draft + trades + teams ≈ 22 calls). */}
          {selected.size + 1 >= 10 && (() => {
            const est = upfrontEstimateMinutes(selected.size + 1);
            return (
              <div style={{
                padding: "10px 14px",
                background: "rgba(234, 179, 8, 0.08)",
                border: "1px solid rgba(234, 179, 8, 0.35)",
                borderRadius: 8,
                color: theme.colors.status.warning,
                fontFamily: theme.fonts.body, fontSize: 13,
                lineHeight: 1.5,
              }}>
                <strong>Heads up:</strong> {selected.size + 1} seasons will take roughly{" "}
                <strong>{est.low}–{est.high} minutes</strong> due to Yahoo's API rate
                limits. You can leave this tab open — the fetch keeps running.
              </div>
            );
          })()}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button style={SECONDARY_BTN} onClick={() => setStep("picking")}>← Different league</button>
            <button style={PRIMARY_BTN} onClick={handleConfirm}>
              Continue ({selected.size + 1} season{selected.size + 1 !== 1 ? "s" : ""})
            </button>
          </div>
        </>
      )}

      {step === "aliasing-loading" && (
        <FetchProgress
          title="Loading owner names..."
          message={progress.message || "Fetching team rosters for the alias step..."}
          current={progress.current}
          total={progress.total}
        />
      )}

      {step === "aliasing" && seasonsTeams && (
        <AliasPanel
          rawNames={extractRawOwners("yahoo", seasonsTeams)}
          leagueId={currentLeague?.league_key}
          platform="yahoo"
          seasons={seasonsTeams}
          onConfirm={handleAliasConfirm}
        />
      )}

      {step === "fetching" && (
        <FetchProgress
          title="Building your league's almanac..."
          message={progress.message || "Calling Yahoo (rate-limited at ~1 request/second)..."}
          current={progress.current}
          total={progress.total}
          seasonCount={confirmedLeagues?.length}
        />
      )}
    </div>
  );
}

// ============================================================================
// SHARED PIECES
// ============================================================================

// Owner-alias panel (task #26). Inserted between season-selection and the
// multi-season fetch. Shows every detected owner with raw nickname (read-
// only) + editable canonical name (autofilled via suggestCanonicalName).
// Persists per-league in localStorage so revisits keep the mapping.
function AliasPanel({ rawNames, leagueId, platform, seasons, onConfirm }) {
  const storageKey = `recap_aliases_v1__${platform}__${leagueId || "anon"}`;
  const [aliasMap, setAliasMap] = useState(() => {
    let stored = {};
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) stored = JSON.parse(raw) || {};
    } catch { /* storage unavailable */ }
    const initial = {};
    for (const name of rawNames) {
      // Prior visit's edit wins over the auto-fill heuristic.
      initial[name] = stored[name] || suggestCanonicalName(name);
    }
    return initial;
  });

  // Same-season collisions (Yahoo only): two teams in one season resolving to
  // the same owner — kept SEPARATE so W/L/PF aren't double-counted. Reacts to
  // the alias edits above (aliasing two coexisting names to one creates a
  // collision). Purely informational; separation is always correct.
  const collisions = useMemo(
    () => (platform === "yahoo" && seasons ? detectSameSeasonCollisions(seasons, aliasMap) : []),
    [platform, seasons, aliasMap]
  );

  const finish = (skip) => {
    const finalMap = skip ? {} : { ...aliasMap };
    try { localStorage.setItem(storageKey, JSON.stringify(finalMap)); } catch { /* ignore */ }
    onConfirm(finalMap);
  };

  return (
    <>
      <SectionHeader
        title="Owner names"
        subtitle={`We found ${rawNames.length} owner${rawNames.length !== 1 ? "s" : ""}. Edit any name that doesn't match how you want it shown, or skip to use the raw nicknames.`}
      />
      {collisions.length > 0 && (
        <div style={{
          fontSize: 12, color: theme.colors.text.muted, fontFamily: theme.fonts.body,
          background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`,
          borderRadius: 8, padding: "8px 12px", marginTop: 10, lineHeight: 1.5,
        }}>
          <span style={{ color: theme.colors.status.warning, fontWeight: theme.fontWeight.bold }}>Kept separate: </span>
          {collisions.slice(0, 4).map((c, i) => (
            <span key={`${c.season}|${c.name}`}>
              {i > 0 ? "; " : ""}{c.season} — two teams both map to “{c.name}”
            </span>
          ))}
          {collisions.length > 4 ? `; +${collisions.length - 4} more` : ""}
          {" "}— counted as separate owners so their records aren’t merged.
        </div>
      )}
      {/* A1.6 R2 #12 — `maxHeight: 360` fixed-height was clipping the
          last row on phones and could push Continue off-screen. Clamp
          the inner scroll to ~50vh so the action buttons stay reachable
          on small viewports. flex-wrap on each row lets the input drop
          to its own line when there isn't room for 140px name + arrow
          + input on one line. paddingBottom keeps the last visible row
          off the scroll container's bottom edge. */}
      <div className="recap-alias-list" style={{
        display: "flex", flexDirection: "column", gap: 8,
        maxHeight: "min(360px, 50vh)", overflowY: "auto",
        paddingBottom: 4,
      }}>
        {rawNames.map((raw) => (
          <div key={raw} className="recap-alias-row" style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            background: theme.colors.bg.surface,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 8, padding: "8px 12px",
          }}>
            <span style={{
              minWidth: 140, flexShrink: 0,
              color: theme.colors.text.muted, fontSize: 12,
              fontFamily: theme.fonts.body,
              fontStyle: "italic",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }} title={raw}>{raw}</span>
            <span style={{ color: theme.colors.text.faint, fontSize: 13 }}>→</span>
            <input
              type="text"
              value={aliasMap[raw] || ""}
              onChange={(e) => setAliasMap({ ...aliasMap, [raw]: e.target.value })}
              style={{
                flex: "1 1 160px", minWidth: 0,
                background: theme.colors.bg.raised,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: 6,
                padding: "6px 10px",
                color: theme.colors.text.primary,
                fontFamily: theme.fonts.body, fontSize: 14,
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button style={SECONDARY_BTN} onClick={() => finish(true)}>Skip — use raw names</button>
        <button style={PRIMARY_BTN} onClick={() => finish(false)}>Continue</button>
      </div>
    </>
  );
}

// Renders the EXACT / LIKELY (name) / OWNERS N% badge tier set per
// season-confirmation row. Owner overlap is the primary signal — when
// present, it shows alongside any name match so the user can judge.
function CandidateTags({ candidate }) {
  const { confidence, nameMatch, ownerMatch, ownerOverlapPct, sharedCount,
          teamsMatch, teamNameOverlapPct, teamNameShared,
          managersMatch, nicknameOverlapPct, nicknameShared } = candidate;
  const tags = [];
  const ownerFired = ownerMatch === "high" || ownerMatch === "medium";

  if (nameMatch === "exact") {
    tags.push({ label: "EXACT (name)", color: theme.colors.status.success });
  } else if (nameMatch === "likely") {
    tags.push({ label: "LIKELY (name)", color: theme.colors.status.warning });
  }
  if (ownerFired) {
    const pct = Math.round((ownerOverlapPct || 0) * 100);
    tags.push({
      label: `OWNERS ${pct}%`,
      color: ownerMatch === "high" ? theme.colors.status.success : theme.colors.status.warning,
    });
  } else if (sharedCount && sharedCount > 0 && !nameMatch) {
    // Shared-count rescue tier — Jaccard was below the medium floor but
    // the raw count of returning managers passed the absolute/relative
    // thresholds. Surface as `SHARED N MANAGERS` so the user understands
    // why this fuzzy candidate (often a very old season) is being offered.
    tags.push({
      label: `SHARED ${sharedCount} MANAGER${sharedCount === 1 ? "" : "S"}`,
      color: theme.colors.status.warning,
    });
  }
  // MANAGERS — nickname overlap (self-excluded). Account-level nicknames are
  // the strongest owner signal surviving guid redaction; the tier caps at
  // 'medium' (never auto-selects). Always warning color, distinct from OWNERS.
  // Shown when the owner signal didn't already fire.
  if (managersMatch === "medium" && !ownerFired) {
    const pct = Math.round((nicknameOverlapPct || 0) * 100);
    tags.push({ label: `MANAGERS ${pct}%`, color: theme.colors.status.warning });
  } else if (nicknameShared && nicknameShared > 0 && !nameMatch && !ownerFired &&
             managersMatch !== "medium" && !(sharedCount && sharedCount > 0)) {
    tags.push({
      label: `SHARED ${nicknameShared} MANAGER${nicknameShared === 1 ? "" : "S"}`,
      color: theme.colors.status.warning,
    });
  }
  // Team-NAME overlap — the fallback lineage signal once Yahoo redacts owner
  // guids. Distinct from OWNERS (weaker: names can coincide). Shown when the
  // owner signal didn't already fire, so the user sees WHY a redaction-era
  // candidate is offered.
  if ((teamsMatch === "high" || teamsMatch === "medium") && ownerMatch !== "high" && ownerMatch !== "medium") {
    const pct = Math.round((teamNameOverlapPct || 0) * 100);
    tags.push({
      label: `TEAMS ${pct}%`,
      color: teamsMatch === "high" ? theme.colors.status.success : theme.colors.status.warning,
    });
  } else if (teamNameShared && teamNameShared > 0 && !nameMatch &&
             ownerMatch == null && teamsMatch !== "high" && teamsMatch !== "medium" &&
             !(sharedCount && sharedCount > 0)) {
    tags.push({
      label: `SHARED ${teamNameShared} TEAM NAME${teamNameShared === 1 ? "" : "S"}`,
      color: theme.colors.status.warning,
    });
  }
  // Fallback when neither name nor owner tag fired but the row was still
  // surfaced (shouldn't happen given chooseTier, but guards against drift).
  if (tags.length === 0 && confidence) {
    tags.push({
      label: confidence.toUpperCase(),
      color: confidence === "exact" ? theme.colors.status.success : theme.colors.status.warning,
    });
  }
  return (
    <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
      {tags.map((t) => (
        <span key={t.label} style={{
          color: t.color, fontSize: 10, letterSpacing: 1,
          textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
          fontFamily: theme.fonts.display,
        }}>{t.label}</span>
      ))}
    </span>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div>
      <div style={{
        fontSize: 22, fontWeight: theme.fontWeight.black,
        color: theme.colors.text.primary, letterSpacing: "-0.5px",
      }}>{title}</div>
      <div style={{
        fontSize: 13, color: theme.colors.text.muted,
        fontFamily: theme.fonts.body, marginTop: 6,
      }}>{subtitle}</div>
    </div>
  );
}

function FetchProgress({ title, message, current, total, seasonCount }) {
  const pct = total ? Math.min(100, Math.round((current / total) * 100)) : null;
  // A1 Patch C — visible elapsed timer. Lets users see fetch IS
  // progressing even when no individual progress message fires for
  // multiple seconds (Yahoo throttle gaps).
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const startedAt = performance.now();
    const id = setInterval(() => {
      setElapsedSec(Math.floor((performance.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedLabel = elapsedSec < 60
    ? `${elapsedSec}s`
    : `${Math.floor(elapsedSec / 60)}m ${String(elapsedSec % 60).padStart(2, "0")}s`;

  // Time remaining: once ≥2 seasons' worth of progress is in, show the rolling
  // ETA (observed avg × seasons left — absorbs rate-limit stalls). Before that,
  // fall back to the upfront "roughly X–Y min" estimate. Phase progress maps to
  // seasons via current/total (the fetch runs a fixed set of phases per season).
  let remainingLabel = null;
  if (seasonCount) {
    const seasonsDone = total ? seasonCount * (current / total) : 0;
    const etaSec = rollingEtaSec(elapsedSec, seasonsDone, seasonCount);
    if (etaSec != null) {
      remainingLabel = fmtRemaining(etaSec);
    } else {
      const u = upfrontEstimateMinutes(seasonCount);
      remainingLabel = u ? `roughly ${u.low}–${u.high} min` : null;
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{
        fontSize: 16, fontWeight: theme.fontWeight.heavy, color: theme.colors.text.primary,
      }}>{title}</div>
      <div style={{
        fontSize: 13, color: theme.colors.text.secondary, fontFamily: theme.fonts.body, minHeight: 20,
      }}>{message}</div>
      <div style={{
        width: "100%", height: 6, borderRadius: 3,
        background: theme.colors.bg.surface, overflow: "hidden",
        border: `1px solid ${theme.colors.border.subtle}`,
      }}>
        <div style={{
          height: "100%",
          width: pct != null ? `${pct}%` : "30%",
          background: theme.colors.brand.gradient,
          transition: "width 0.3s ease",
          animation: pct == null ? "recap-pulse 1.4s ease-in-out infinite" : undefined,
        }} />
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between",
        fontSize: 11, color: theme.colors.text.muted,
        fontFamily: theme.fonts.body,
      }}>
        <span>Elapsed: {elapsedLabel}</span>
        <span style={{ display: "flex", gap: 10 }}>
          {remainingLabel && <span>{remainingLabel}</span>}
          {pct != null && <span>{pct}%</span>}
        </span>
      </div>
    </div>
  );
}
